import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PlannedQuestion, Vertical } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { Info } from "../../components/icons.js";

/** GET /candidacies/{id}/questions — what THIS candidate will actually be asked, with provenance. */
interface CandidacyQuestions {
  source: "candidacy" | "requisition" | "bank";
  planSource: "resume_tailored" | "requisition" | "manual" | null;
  generatedAt: string | null;
  questions: PlannedQuestion[];
}

/**
 * Provenance badge — the ONE honest label for how a candidate's interview plan was authored. Shared
 * by the funnel panel below and the post-interview report so they never disagree.
 */
export function planBadge(
  source: CandidacyQuestions["source"],
  planSource: CandidacyQuestions["planSource"],
): { label: string; cls: string } {
  if (planSource === "manual") return { label: "Edited by you", cls: "pill pv" };
  if (planSource === "resume_tailored" || source === "candidacy")
    return { label: "Resume-tailored", cls: "pill v" };
  return { label: "Standard plan", cls: "pill n" };
}

const newId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `tmp-${Math.random().toString(36).slice(2)}`;

/**
 * PER-CANDIDATE questions — the escape hatch above automatic tailoring. Shows exactly what one
 * candidate will be asked (with provenance), and lets the recruiter regenerate from that person's
 * resume or hand-edit + save for a high-stakes hire. Saving marks the plan "Edited by you". Best
 * used before the interview starts; editing after it has run won't change a completed interview.
 */
export function CandidacyQuestionsPanel({
  candidacyId,
  vertical,
}: {
  candidacyId: string;
  /** The requisition's vertical — turns the stored competencyIds into the labels the recruiter
   *  chose from. Undefined only while it loads, or if the vertical was since deactivated. */
  vertical: Vertical | undefined;
}): ReactNode {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<PlannedQuestion[] | null>(null);

  const plan = useQuery({
    queryKey: ["candidacy-questions", candidacyId],
    queryFn: () => api.get<CandidacyQuestions>(`/candidacies/${candidacyId}/questions`),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (plan.data && draft === null) setDraft(plan.data.questions);
  }, [plan.data, draft]);

  const regen = useMutation({
    mutationFn: () =>
      api.post<{ tailored: boolean; questions: PlannedQuestion[]; reason?: string }>(
        `/candidacies/${candidacyId}/questions/generate`,
        {},
      ),
    onSuccess: (res) => {
      // Only adopt a plan that was ACTUALLY regenerated. On a fail-soft result (thin resume, LLM
      // down) the server returns the generic shared spine and does NOT overwrite the stored plan —
      // adopting it into the draft would misrepresent the plan and let Save clobber a hand-edited
      // set with generic questions. Clear the draft so the refetch re-hydrates the true stored plan.
      setDraft(res.tailored ? res.questions : null);
      void queryClient.invalidateQueries({ queryKey: ["candidacy-questions", candidacyId] });
    },
  });

  const save = useMutation({
    mutationFn: (questions: PlannedQuestion[]) =>
      api.put<CandidacyQuestions>(`/candidacies/${candidacyId}/questions`, { questions }),
    // Reconcile the draft to the server's canonical (trimmed, id-assigned) result so the panel
    // reads "Saved" and drops out of the dirty state even when a prompt had surrounding whitespace.
    onSuccess: (res) => {
      setDraft(res.questions);
      void queryClient.invalidateQueries({ queryKey: ["candidacy-questions", candidacyId] });
    },
  });

  if (plan.isLoading) return <p className="mut sm">Loading this candidate's questions…</p>;
  if (plan.isError || !plan.data)
    return <p className="mut sm">Couldn't load this candidate's questions right now.</p>;

  const badge = planBadge(plan.data.source, plan.data.planSource);
  const items = draft ?? plan.data.questions;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(plan.data.questions);

  const update = (id: string, prompt: string): void =>
    setDraft(items.map((q) => (q.id === id ? { ...q, prompt } : q)));
  const remove = (id: string): void => setDraft(items.filter((q) => q.id !== id));
  // A hand-added question is untargeted (0100): the evaluator reads every answer against every
  // competency, so there is no competency to pick.
  const add = (): void => {
    setDraft([...items, { id: newId(), competencyId: null, prompt: "", kind: "behavioral" }]);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span className={badge.cls}>{badge.label}</span>
        <span className="mut sm">{items.length} questions</span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="rq-btn ghost sm"
          style={{ padding: "5px 11px", fontSize: 12.5 }}
          disabled={regen.isPending}
          onClick={() => regen.mutate()}
          title="Regenerate this candidate's questions from their resume"
        >
          {regen.isPending ? "Regenerating…" : "Regenerate from resume"}
        </button>
      </div>

      {regen.data && !regen.data.tailored && (
        <div className="banner warn" style={{ marginBottom: 10, fontSize: 12.5 }}>
          <Info />
          <div>
            {regen.data.reason ??
              "Couldn't build a resume-tailored set — this candidate keeps their current plan (nothing was changed)."}
          </div>
        </div>
      )}

      {plan.data.source !== "candidacy" && (
        <div className="banner info" style={{ marginBottom: 10, fontSize: 12.5 }}>
          <Info />
          <div>
            This candidate is on the shared plan — no resume-tailored set yet. "Regenerate from
            resume" builds one from their scored resume (needs a scored resume on file).
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((q, i) => (
          <div key={q.id} className="card" style={{ padding: "10px 12px", background: "var(--surface-2)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 12.5 }}>Question {i + 1}</span>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                aria-label="Remove question"
                onClick={() => remove(q.id)}
                style={{ border: "none", background: "transparent", color: "var(--ink-mute)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}
              >
                ×
              </button>
            </div>
            {/* QA #60: a typical ~200-char prompt wrapped to 3 lines and rows=2 clipped it inside
                this already-scrolling modal — the recruiter had to click in and scroll to read
                the whole question. 3 rows fits the common case. */}
            <textarea
              className="rq-in"
              value={q.prompt}
              maxLength={1000}
              rows={3}
              onChange={(e) => update(q.id, e.target.value)}
              style={{ minHeight: 72 }}
            />
          </div>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
        <button type="button" className="rq-btn ghost sm" style={{ padding: "5px 11px", fontSize: 12.5 }} onClick={add}>
          + Add question
        </button>
        <span style={{ flex: 1 }} />
        {save.isSuccess && !dirty && <span className="mut sm" style={{ color: "var(--good)" }}>Saved</span>}
        <button
          type="button"
          className="rq-btn primary sm"
          style={{ padding: "6px 14px", fontSize: 12.5 }}
          disabled={
            !dirty ||
            save.isPending ||
            items.length === 0 || // an empty save would silently drop this candidate back to the shared plan
            items.some((q) => q.prompt.trim().length < 4)
          }
          onClick={() => draft && save.mutate(draft.map((q) => ({ ...q, prompt: q.prompt.trim() })))}
        >
          {save.isPending ? "Saving…" : "Save for this candidate"}
        </button>
      </div>
    </div>
  );
}
